import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { DataTable } from "@/components/data";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";

import {
  getSoldCostMonth,
  getSoldCostMonths,
  postProductCostLoad,
  SoldCostsError,
  type ProductCostLoad,
  type SoldCostProduct,
} from "./api";
import { COPY, messageFor, money, signedPct } from "./format";
import { MONTH_STATE_COPY, monthState } from "./monthState";
import { isCase, productCostLeaf, productCostParity, productCostRows, productCostTotal } from "./productCostModel";

const ENTITIES = [
  { value: "LTD", label: "LTD (UK)" },
  { value: "OU", label: "OU (EU)" },
  { value: "LLC", label: "LLC (MENA)" },
] as const;

type ProductSort = { id: string; direction: "asc" | "desc" } | null;

export function displayDate(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(new Date(value));
}

function monthName(value: string): string {
  return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${value.slice(0, 7)}-01T00:00:00Z`)).replace(/^Sept /, "Sep ");
}

export function monthLabel(value: string, flags: { closed?: boolean; waiting_for_sheet?: boolean; history?: boolean; held?: boolean }): string {
  const label = new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${value.slice(0, 7)}-01T00:00:00Z`)).replace(/^Sept /, "Sep ");
  const state = monthState(flags);
  if (state === "waiting") return `${label} (waiting for the sheet)`;
  return state === "open" ? `${label} (open)` : label;
}

export function loadErrorMessage(error: unknown): string {
  const value = error as SoldCostsError;
  if (value?.status === 403) return "You need edit access to load the sheet.";
  if (value?.status === 502) {
    if (value.code === "wrong_tab") return "The sheet's first tab is not DATA. Move the DATA tab first and load again.";
    if (value.code === "unknown_region") return "A row has a region other than UK, EU or MENA. Fix the sheet and load again.";
    if (value.code === "fractional_qty") return "A row has a fractional quantity. Fix the sheet and load again.";
    if (value.code === "drive_unreadable") return "The engine cannot read the sheet. Check it is shared with the pfc-engine service account.";
    return "The sheet load failed. The engine recorded the cause in its run log; ask Bojan to check it.";
  }
  return messageFor(error);
}

export function loadResultText(result: ProductCostLoad): string {
  if (result.rows === 0 && result.skipped === 0) return "The sheet has not changed since the last load.";
  return `Loaded ${result.rows} rows from the sheet updated ${displayDate(result.source_modified_at)}.`;
}

function sortRows(rows: SoldCostProduct[], sort: ProductSort): SoldCostProduct[] {
  if (!sort) return rows;
  const direction = sort.direction === "asc" ? 1 : -1;
  const value = (row: SoldCostProduct): string | number => {
    if (sort.id === "tracking") return row.tracking;
    if (sort.id === "item_name") return row.item_name ?? "";
    if (sort.id === "qty") return Number(row.qty);
    if (sort.id === "per_unit_gbp") return Number(row.per_unit_gbp);
    return Number(row.product_amount_gbp);
  };
  return rows.map((row, index) => ({ row, index })).sort((a, b) => {
    const left = value(a.row);
    const right = value(b.row);
    const compared = typeof left === "number" && typeof right === "number"
      ? left - right
      : String(left).localeCompare(String(right), "en-GB");
    return compared === 0 ? a.index - b.index : compared * direction;
  }).map(({ row }) => row);
}

export function ProductCostView() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const entity = search.sc_entity === "OU" || search.sc_entity === "LLC" ? search.sc_entity : "LTD";
  const set = (patch: Record<string, unknown>) => void navigate({ to: ".", search: { ...search, ...patch }, replace: true });
  const [sort, setSort] = useState<ProductSort>(null);

  const monthsQuery = useQuery({
    queryKey: ["pcogs", "sold-costs", "months", entity],
    queryFn: () => getSoldCostMonths(entity),
    retry: 1,
  });
  const visibleMonths = useMemo(
    () => (monthsQuery.data?.data ?? []).filter((row) => row.visible).sort((a, b) => b.month.localeCompare(a.month)),
    [monthsQuery.data],
  );
  const requestedMonth = typeof search.sc_month === "string" ? search.sc_month : "";
  const selectedMonth = visibleMonths.some((row) => row.month === requestedMonth) ? requestedMonth : visibleMonths[0]?.month ?? "";
  const selectedRow = visibleMonths.find((row) => row.month === selectedMonth);
  const monthQuery = useQuery({
    queryKey: ["pcogs", "sold-costs", "month", entity, selectedMonth],
    queryFn: () => getSoldCostMonth(entity, selectedMonth),
    enabled: selectedMonth !== "",
    retry: 1,
  });

  const load = useMutation({
    mutationFn: postProductCostLoad,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pcogs", "sold-costs", "months"] });
      void queryClient.invalidateQueries({ queryKey: ["pcogs", "sold-costs", "month"] });
    },
  });

  const month = monthQuery.data?.data;
  const servedRows = month ? productCostRows(month) : [];
  const rows = useMemo(() => sortRows(servedRows, sort), [servedRows, sort]);
  const parity = month ? productCostParity(month) : null;
  const currency = month?.currency;
  const sourceModifiedAt = servedRows[0]?.source_modified_at;
  const columns: ColumnDef<SoldCostProduct, unknown>[] = [
    { id: "tracking", accessorKey: "tracking", header: "Item code", meta: { sortable: true } },
    { id: "item_name", accessorKey: "item_name", header: "Item name", meta: { sortable: true } },
    { id: "qty", header: "Qty", cell: ({ row }) => {
      const item = row.original;
      const qty = item.qty === undefined ? "—" : String(Math.trunc(Number(item.qty)));
      return isCase(item) ? `${qty} cases` : qty;
    }, meta: { sortable: true, align: "right" } },
    { id: "per_unit_gbp", header: "Cost per unit (GBP)", cell: ({ row }) => {
      const item = row.original;
      const caseRow = isCase(item);
      const local = money(item.per_unit_local, item.local_currency, 4);
      const title = `${local} per ${caseRow ? "case" : "unit"} in ${item.local_currency ?? "local currency"}`;
      return <span title={title}>{money(item.per_unit_gbp, "GBP", 4)}{caseRow ? "/case" : ""}</span>;
    }, meta: { sortable: true, align: "right" } },
    { id: "product_amount_gbp", header: "Item cost (GBP)", cell: ({ row }) => {
      const item = row.original;
      return <span title={money(item.product_amount, item.local_currency)}>{money(item.product_amount_gbp, "GBP")}</span>;
    }, meta: { sortable: true, align: "right" } },
  ];

  const error = monthsQuery.error ?? monthQuery.error;
  const unavailable = error instanceof SoldCostsError && (error.status === 404 || error.status === 405);
  const loading = monthsQuery.isPending || (selectedMonth !== "" && monthQuery.isPending);
  const noMonth = !monthsQuery.isPending && !monthsQuery.error && visibleMonths.length === 0;
  const noItemSource = !!month && productCostLeaf(month) === null;
  const waitingForSheet = !!month && (productCostLeaf(month)?.waiting_for_sheet === true || productCostLeaf(month)?.status === "product_cost_missing");
  const detail = month?.run?.detail;

  return <div className="flex flex-col gap-4">
    <div className="flex flex-wrap items-end gap-5">
      <fieldset>
        <legend className="mb-2 text-sm font-bold">Company</legend>
        <RadioGroup
          className="flex flex-wrap gap-3"
          value={entity}
          onValueChange={(value) => set({ sc_entity: value, sc_month: undefined })}
          aria-label="Company"
        >
          {ENTITIES.map((item) => <label key={item.value} className="flex items-center gap-2 text-sm">
            <RadioGroupItem value={item.value} data-nonmutating />{item.label}
          </label>)}
        </RadioGroup>
      </fieldset>
      <label className="flex flex-col gap-2 text-sm font-bold">
        Month
        <Select value={selectedMonth} onValueChange={(value) => set({ sc_month: value })} disabled={visibleMonths.length === 0}>
          <SelectTrigger data-nonmutating aria-label="Month" className="min-w-40"><SelectValue placeholder="Choose a month" /></SelectTrigger>
          <SelectContent>{visibleMonths.map((row) => <SelectItem key={row.month} value={row.month}>{monthLabel(row.month, row)}</SelectItem>)}</SelectContent>
        </Select>
      </label>
    </div>
    <p className="text-sm text-ks-grey-1">Months appear once Precision Fulfilment Costs has closed them, or once the engine has computed their cost of goods without a PFC close.</p>

    <div className="flex flex-col items-start gap-2">
      <Button type="button" variant="outline" data-nonmutating disabled={load.isPending} onClick={() => load.mutate()}>
        {load.isPending ? "Loading…" : "Load from sheet"}
      </Button>
      {load.data && <div className="text-sm text-ks-ink">
        <p>{loadResultText(load.data.data)}</p>
        {!!load.data.data.detail.case_rows_reviewed?.length && <p>Case rows taken as reviewed: {load.data.data.detail.case_rows_reviewed.join(", ")}.</p>}
      </div>}
      {load.error && <p role="alert" className="text-ks-danger">{loadErrorMessage(load.error)}</p>}
      <p className="text-sm text-ks-grey-1">New sheet figures reach an open month at its next scheduled run. {month?.history || selectedRow?.history ? "For a month without a PFC close, the engine runbook rewrites it." : "For a closed month, ask Bojan to recalculate it."}</p>
    </div>

    {error ? (
      unavailable ? <p className="text-ks-ink">{COPY.unavailable}</p> : <p role="alert" className="text-ks-danger">{messageFor(error)}</p>
    ) : noMonth ? (
      <p className="text-ks-ink">No Sold-COGS month has been computed for this company yet.</p>
    ) : waitingForSheet && month ? (
      <div className="flex flex-col gap-2 text-ks-ink">
        <p>{MONTH_STATE_COPY.waiting}: the Unified sheet has no rows for {monthName(month.month)} yet. Load the sheet once it carries the month, then recalculate the month.</p>
        {parity?.product_cost_missing && <p className="text-sm">{MONTH_STATE_COPY.waiting} · {parity.no_account_pool ? "no Xero account amount for this month" : `Xero account ${money(parity.account_total, currency)}`}</p>}
      </div>
    ) : noItemSource ? (
      <p className="text-ks-ink">Cost of Product from the sheet is not in this month yet. Load the sheet, then recalculate the month.</p>
    ) : <>
      {month?.history && <p className="text-ks-ink">{MONTH_STATE_COPY.historyTitle}</p>}
      {sourceModifiedAt && <p className="text-sm text-ks-grey-1">From the Unified Monthly Sales by SKU sheet, updated {displayDate(sourceModifiedAt)}.</p>}
      {detail?.product_cost_stale && <p role="alert" className="text-ks-danger">The last sheet load failed; these figures come from the previous load.</p>}
      <DataTable
        columns={columns}
        rows={rows}
        pending={loading}
        sort={sort ?? undefined}
        onSort={(id) => setSort((previous) => ({ id, direction: previous?.id === id && previous.direction === "asc" ? "desc" : "asc" }))}
        page={{ pageIndex: 0, pageSize: Math.max(60, rows.length), rowCount: rows.length }}
        onPageChange={() => {}}
        emptyMessage="Cost of Product from the sheet is not in this month yet. Load the sheet, then recalculate the month."
        caption="Cost of Product from the Unified Monthly Sales by SKU sheet"
        footer={month && <TableRow className="font-bold">
          <TableCell colSpan={4}>Total</TableCell>
          <TableCell className="text-right">{money(productCostTotal(servedRows), "GBP")}</TableCell>
        </TableRow>}
      />
      {parity && <div className="text-sm text-ks-ink">
        <p>
          Sheet total {money(parity.item_total, currency)} · allocated to sold units {money(parity.allocated, currency)} · without units {money(parity.unallocated, currency)} · {parity.no_account_pool ? "no Xero account amount for this month" : `Xero account ${money(parity.account_total, currency)}`} · difference {money(parity.residual, currency)} ({signedPct(parity.residual_pct === null ? null : String(Number(parity.residual_pct) * 100))})
          {parity.units_gap !== "0.0000" && <> · units gap {money(parity.units_gap, currency)}</>}
        </p>
        {!!detail?.item_without_units?.length && <p>Items with no sold units this month: {detail.item_without_units.join(", ")}.</p>}
        {!!detail?.case_without_registry?.length && <p>Case items missing from the channel registry: {detail.case_without_registry.join(", ")}.</p>}
      </div>}
    </>}
  </div>;
}
